import { ElementType, forwardRef, ReactNode, Ref } from 'react';

import { styled, Typography, TypographyProps } from '@mui/material';

import clsx from 'clsx';

import { generateClasses } from '../utils/helper';
import Button, { Props as ButtonProps } from './Button';

const headerClasses = {
  ...generateClasses('Header', ['action', 'title']),
};

export { headerClasses };

const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(5),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(3),
  minHeight: 68,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.grey[900],
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  position: 'relative',
}));

const Title = styled((props: TypographyProps) => (
  <Typography variant="h4Bold" color="inherit" noWrap {...props} />
))(() => ({
  flex: 1,
  whiteSpace: 'pre-wrap',
}));

const Action = styled(
  forwardRef(<C extends ElementType<any> = 'button'>(props: ButtonProps<C>, ref: Ref<HTMLButtonElement>) => (
    <Button
      ref={ref}
      variant="text"
      color="primary"
      size="small"
      {...props}
      iconOnly
      title={props.title}
      className={clsx(headerClasses.action, props.className)}
    />
  ))
)(({ theme }) => ({
  [`&.${headerClasses.action}`]: {
    color: theme.palette.grey[500],
  },
}));

export type HeaderActionProps<C extends ElementType<any> = 'button'> = ButtonProps<C>;

export interface Props<L extends ElementType<any> = 'button', R extends ElementType<any> = 'button'> {
  children: ReactNode;
  className?: string;
  id?: string;
  left?: ButtonProps<L> | (() => ReactNode);
  right?: ButtonProps<R> | (() => ReactNode);
  classes?: Partial<typeof headerClasses>;
}

const Header = <L extends ElementType<any> = 'button', R extends ElementType<any> = 'button'>({
  children,
  className,
  id,
  left,
  right,
  classes,
}: Props<L, R>) => {
  return (
    <Root className={className}>
      {typeof left === 'function'
        ? left()
        : left && <Action className={classes?.action} edge="start" {...left} />}
      {typeof children === 'string' ? (
        <Title id={id} className={clsx(headerClasses.title, classes?.title)}>
          {children}
        </Title>
      ) : (
        children
      )}
      {typeof right === 'function'
        ? right()
        : right && <Action className={classes?.action} edge="end" {...right} />}
    </Root>
  );
};

export default Header;
